import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import {
  Box,
  Chip,
  Collapse,
  Drawer,
  IconButton,
  TextField,
  Typography,
  Divider,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import StarIcon from '@mui/icons-material/Star';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import AiMessageBubble from './AiMessageBubble';
import AiApprovalCard from './AiApprovalCard';

const COMPACT_WIDTH = 480;
const EXPANDED_WIDTH = 750;
const APPBAR_HEIGHT = 48;

const WELCOME_MESSAGES = {
  auto:      "Hi, I'm **Blaack AI**! Just ask me anything — about orders, products, discounts, or reports. I'll automatically route to the right module.",
  products:  "Hi, I'm **Blaack AI**! I'm handling a **Products** query.",
  orders:    "Hi, I'm **Blaack AI**! I'm handling an **Orders** query.",
  discounts: "Hi, I'm **Blaack AI**! I'm handling a **Discounts** query.",
};

const getWelcomeMessage = (mod) => ({
  id: 'welcome',
  role: 'assistant',
  content: WELCOME_MESSAGES[mod] ?? WELCOME_MESSAGES.auto,
});

const ORDER_STATUSES = [
  'Submitted', 'Confirmed', 'Dispatched', 'ReadyToDelivered', 'Delivered', 'Cancelled', 'Failed',
];

const AiCopilotDrawer = () => {
  const navigate = useNavigate();
  const allRegions = useSelector((state) => state.counter?.allRegionList ?? []);

  // Build a deduplicated list of city names from the region list
  const regionNames = useMemo(() => {
    const seen = new Set();
    const names = [];
    allRegions.forEach((r) => {
      const name = r?.city?.name;
      if (name && !seen.has(name)) {
        seen.add(name);
        names.push(name);
      }
    });
    return names.sort();
  }, [allRegions]);

  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [bubblePosition, setBubblePosition] = useState({ x: null, y: null });
  const [messages, setMessages] = useState([getWelcomeMessage('auto')]);
  const [feedbackByRequest, setFeedbackByRequest] = useState({});
  const [pendingApproval, setPendingApproval] = useState(null);
  const [loading, setLoading] = useState(false);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [lastRoutedModule, setLastRoutedModule] = useState(null); // set from API response
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [activeRegion, setActiveRegion] = useState(null);
  const [activeStatus, setActiveStatus] = useState(null);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const drawerWidth = expanded ? EXPANDED_WIDTH : COMPACT_WIDTH;

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, pendingApproval, scrollToBottom]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [open]);

  // Initialize bubble position (bottom-right with margin)
  useEffect(() => {
    if (bubblePosition.x === null) {
      setBubblePosition({
        x: window.innerWidth - 80,
        y: window.innerHeight - 120,
      });
    }
  }, [bubblePosition.x]);

  const sendMessage = async () => {
    const text = prompt.trim();
    if (!text || loading) return;

    // Append selected filter context so the model uses exact values
    let contextualPrompt = text;
    const parts = [];
    if (activeRegion) parts.push(`region: ${activeRegion}`);
    if (activeStatus) parts.push(`status: ${activeStatus}`);
    if (parts.length > 0) contextualPrompt += ` [filter — ${parts.join(', ')}]`;

    const userMessage = { id: `user-${Date.now()}`, role: 'user', content: text };
    setMessages((prev) => [...prev, userMessage]);
    setPrompt('');
    setLoading(true);
    setPendingApproval(null);

    try {
      // No module sent — backend router auto-detects from prompt
      const response = await axios.post('ai/chat', { prompt: contextualPrompt });
      const data = response.data;

      if (data.routed_module) setLastRoutedModule(data.routed_module);

      const assistantMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: data.response || data.message || 'No response received.',
        suggestedAction: data.suggested_action || null,
        options: data.options || null,
        requestId: data.request_id || null,
        via: data.routed_module || null,
        confidence: data.router_confidence ?? null,
      };
      setMessages((prev) => [...prev, assistantMessage]);

      if (data.pending_approval) {
        setPendingApproval(data.pending_approval);
      }
    } catch (error) {
      const errData = error?.response?.data;
      const errorMessage = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: errData?.message || 'Something went wrong. Please try again.',
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!pendingApproval) return;
    setApprovalLoading(true);

    try {
      const response = await axios.post('ai/approve', {
        approval_token: pendingApproval.token,
      });
      const data = response.data;

      if (data.success) {
        const successMsg = {
          id: `approval-${Date.now()}`,
          role: 'assistant',
          content: `Approved and executed: ${data.result?.message || data.message || 'done'}.`,
        };
        setMessages((prev) => [...prev, successMsg]);
      } else {
        const failMsg = {
          id: `approval-fail-${Date.now()}`,
          role: 'assistant',
          content: data.message || 'Approval could not be confirmed.',
        };
        setMessages((prev) => [...prev, failMsg]);
      }
    } catch (error) {
      const errData = error?.response?.data;
      const errorMsg = {
        id: `approval-err-${Date.now()}`,
        role: 'assistant',
        content: errData?.message || 'Approval request failed.',
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setPendingApproval(null);
      setApprovalLoading(false);
    }
  };

  const handleDismiss = () => {
    setPendingApproval(null);
  };

  // When admin clicks an option chip, treat it as a new user message.
  // The chip re-sends in the module that produced it (msg.via) — plain chip
  // values like "Madurai" carry no routing keywords, so re-routing them
  // would misroute to the default module.
  const handleOptionClick = useCallback((value, viaModule) => {
    if (loading) return;
    setPrompt('');
    const text = String(value).trim();
    if (!text) return;

    const userMessage = { id: `user-${Date.now()}`, role: 'user', content: text };
    setMessages((prev) => [...prev, userMessage]);
    setLoading(true);
    setPendingApproval(null);

    let contextualPrompt = text;
    const parts = [];
    if (activeRegion) parts.push(`region: ${activeRegion}`);
    if (activeStatus) parts.push(`status: ${activeStatus}`);
    if (parts.length > 0) contextualPrompt += ` [filter — ${parts.join(', ')}]`;

    // Re-send in the module that produced the chip when known — plain chip
    // values carry no routing keywords, so re-routing would misroute.
    // Otherwise omit module and let the backend router auto-detect.
    const payload = { prompt: contextualPrompt };
    if (viaModule) payload.module = viaModule;
    axios.post('ai/chat', payload)
      .then((response) => {
        const data = response.data;
        if (data.routed_module) setLastRoutedModule(data.routed_module);
        const assistantMessage = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: data.response || data.message || 'No response received.',
          suggestedAction: data.suggested_action || null,
          options: data.options || null,
          requestId: data.request_id || null,
          via: data.routed_module || null,
          confidence: data.router_confidence ?? null,
        };
        setMessages((prev) => [...prev, assistantMessage]);
        if (data.pending_approval) setPendingApproval(data.pending_approval);
      })
      .catch((error) => {
        const errData = error?.response?.data;
        setMessages((prev) => [
          ...prev,
          {
            id: `error-${Date.now()}`,
            role: 'assistant',
            content: errData?.message || 'Something went wrong. Please try again.',
          },
        ]);
      })
      .finally(() => setLoading(false));
  }, [loading, activeRegion, activeStatus]);

  // Thumbs up/down for one assistant response; tied to the turn's request_id
  // so a "bad" rating can later be traced to its prompt + tool calls.
  const handleFeedback = async (requestId, content, rating, comment, viaModule) => {
    if (!requestId) return;
    setFeedbackByRequest((prev) => ({ ...prev, [requestId]: rating }));
    try {
      await axios.post('ai/feedback', {
        request_id: requestId,
        rating,
        comment,
        response_text: content,
        module: viaModule ?? lastRoutedModule ?? 'auto',
      });
    } catch (error) {
      // Revert the optimistic update so the team can retry.
      setFeedbackByRequest((prev) => {
        const next = { ...prev };
        delete next[requestId];
        return next;
      });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Minimize to bubble instead of closing
  const handleMinimize = () => {
    setOpen(false);
    setMinimized(true);
  };

  // Start a new conversation
  const handleNewChat = () => {
    setMessages([getWelcomeMessage('auto')]);
    setPendingApproval(null);
    setPrompt('');
    setActiveRegion(null);
    setActiveStatus(null);
    setLastRoutedModule(null);
  };

  // Reopen from bubble
  const handleBubbleClick = () => {
    setOpen(true);
    setMinimized(false);
  };

  // Drag logic for floating bubble
  const handleBubbleMouseDown = (e) => {
    setDragging(true);
    setDragStart({
      x: e.clientX - bubblePosition.x,
      y: e.clientY - bubblePosition.y,
    });
    e.preventDefault();
  };

  const handleBubbleMouseMove = (e) => {
    if (!dragging) return;
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    // Keep within viewport
    const maxX = window.innerWidth - 56;
    const maxY = window.innerHeight - 56;
    setBubblePosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY)),
    });
  };

  const handleBubbleMouseUp = () => {
    setDragging(false);
    setDragStart(null);
  };

  const handleBubbleClickWithDragCheck = () => {
    // Only reopen if it wasn't a drag (mouse moved less than 5px)
    if (!dragStart) {
      handleBubbleClick();
    }
  };

  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', handleBubbleMouseMove);
      window.addEventListener('mouseup', handleBubbleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleBubbleMouseMove);
        window.removeEventListener('mouseup', handleBubbleMouseUp);
      };
    }
  }, [dragging, dragStart]);

  return (
    <>
      {/* Toolbar trigger */}
      <Tooltip title="Blaack AI">
        <IconButton
          onClick={() => {
            setOpen(true);
            setMinimized(false);
          }}
          size="small"
          aria-label="Open Blaack AI"
          className="blaack-ai-trigger"
          sx={{ ml: 2 }}
        >
          <AutoAwesomeIcon fontSize="medium" />
          <StarIcon className="blaack-ai-star blaack-ai-star-a" sx={{ fontSize: 12 }} />
          <StarIcon className="blaack-ai-star blaack-ai-star-b" sx={{ fontSize: 9 }} />
        </IconButton>
      </Tooltip>

      {/* Floating bubble (when minimized) */}
      {minimized && !open && (
        <Tooltip title="Blaack AI">
          <IconButton
            onMouseDown={handleBubbleMouseDown}
            onClick={handleBubbleClickWithDragCheck}
            size="large"
            className="blaack-ai-bubble"
            sx={{
              position: 'fixed',
              right: bubblePosition.x ? `calc(100% - ${bubblePosition.x + 28}px)` : '24px',
              bottom: bubblePosition.y ? `calc(100% - ${bubblePosition.y + 28}px)` : '24px',
              zIndex: 1500,
              color: 'white',
              boxShadow: 3,
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              '&:hover': { transform: 'scale(1.1)', boxShadow: 4 },
            }}
            aria-label="Open Blaack AI"
          >
            <AutoAwesomeIcon fontSize="large" />
          </IconButton>
        </Tooltip>
      )}

      {/* Drawer */}
      <Drawer
        anchor="right"
        open={open}
        onClose={handleMinimize}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 2,
          transition: 'none',
          '& .MuiDrawer-paper': {
            transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          },
        }}
        PaperProps={{
          sx: {
            width: drawerWidth,
            bgcolor: 'background.paper',
            top: `${APPBAR_HEIGHT}px`,
            height: `calc(100% - ${APPBAR_HEIGHT}px)`,
            borderRadius: '8px 0 0 0',
            boxShadow: -4,
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            <AutoAwesomeIcon sx={{ color: 'primary.main' }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Blaack AI
              </Typography>
              <Typography variant="caption" color="text.secondary">
                AI Admin Copilot
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="New Chat">
              <IconButton onClick={handleNewChat} size="small">
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={expanded ? 'Collapse' : 'Expand'}>
              <IconButton
                onClick={() => setExpanded((prev) => !prev)}
                size="small"
              >
                {expanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Minimize">
              <IconButton onClick={handleMinimize} size="small">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Auto-routing badge — shows which module handled the last prompt */}
        <Box
          sx={{
            px: 2,
            py: 0.75,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Auto-routing:
          </Typography>
          {lastRoutedModule ? (
            <Chip
              label={lastRoutedModule.charAt(0).toUpperCase() + lastRoutedModule.slice(1)}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontSize: '0.7rem', height: 20 }}
            />
          ) : (
            <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
              detected from your prompt
            </Typography>
          )}

          {/* Filter toggle — shown when last routed module was orders */}
          {lastRoutedModule === 'orders' && (
            <Tooltip title={showFilters ? 'Hide filters' : 'Filter by region / status'}>
              <IconButton
                size="small"
                onClick={() => setShowFilters((v) => !v)}
                sx={{ ml: 'auto', color: (activeRegion || activeStatus) ? 'primary.main' : 'text.secondary' }}
              >
                <FilterListIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Quick-filter chips (orders only) */}
        <Collapse in={lastRoutedModule === 'orders' && showFilters}>
          <Box
            sx={{
              px: 1.5,
              py: 1,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'background.default',
            }}
          >
            {/* Region chips */}
            {regionNames.length > 0 && (
              <Box sx={{ mb: 0.75 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Region
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  <Chip
                    label="All"
                    size="small"
                    onClick={() => setActiveRegion(null)}
                    color={activeRegion === null ? 'primary' : 'default'}
                    variant={activeRegion === null ? 'filled' : 'outlined'}
                  />
                  {regionNames.map((name) => (
                    <Chip
                      key={name}
                      label={name}
                      size="small"
                      onClick={() => setActiveRegion(activeRegion === name ? null : name)}
                      color={activeRegion === name ? 'primary' : 'default'}
                      variant={activeRegion === name ? 'filled' : 'outlined'}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Status chips */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Order Status
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                <Chip
                  label="All"
                  size="small"
                  onClick={() => setActiveStatus(null)}
                  color={activeStatus === null ? 'primary' : 'default'}
                  variant={activeStatus === null ? 'filled' : 'outlined'}
                />
                {ORDER_STATUSES.map((s) => (
                  <Chip
                    key={s}
                    label={s}
                    size="small"
                    onClick={() => setActiveStatus(activeStatus === s ? null : s)}
                    color={activeStatus === s ? 'primary' : 'default'}
                    variant={activeStatus === s ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </Collapse>

        {/* Active filter summary badge */}
        {lastRoutedModule === 'orders' && (activeRegion || activeStatus) && (
          <Box sx={{ px: 1.5, py: 0.5, bgcolor: 'action.hover', display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">Active:</Typography>
            {activeRegion && (
              <Chip label={activeRegion} size="small" color="primary" onDelete={() => setActiveRegion(null)} />
            )}
            {activeStatus && (
              <Chip label={activeStatus} size="small" color="secondary" onDelete={() => setActiveStatus(null)} />
            )}
          </Box>
        )}

        {/* Messages */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            pt: 2,
            pb: 1,
            bgcolor: 'background.default',
          }}
        >
          {messages.map((msg) => (
            <AiMessageBubble
              key={msg.id}
              role={msg.role}
              content={msg.content}
              suggestedAction={msg.suggestedAction}
              options={msg.options}
              onActionClick={(url) => navigate(url)}
              onOptionClick={(value, viaModule) => handleOptionClick(value, viaModule)}
              requestId={msg.requestId}
              via={msg.via}
              feedback={msg.requestId ? (feedbackByRequest[msg.requestId] ?? null) : null}
              onFeedback={(rating, comment) => handleFeedback(msg.requestId, msg.content, rating, comment, msg.via)}
            />
          ))}

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', px: 2, mb: 1.5 }}>
              <Box
                sx={{
                  px: 2,
                  py: 1.25,
                  borderRadius: 2,
                  borderBottomLeftRadius: 0,
                  bgcolor: 'action.hover',
                  boxShadow: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <CircularProgress size={14} />
                <Typography variant="body2" color="text.secondary">
                  Blaack AI is thinking...
                </Typography>
              </Box>
            </Box>
          )}

          <AiApprovalCard
            approval={pendingApproval}
            onApprove={handleApprove}
            onDismiss={handleDismiss}
            loading={approvalLoading}
          />

          <div ref={messagesEndRef} />
        </Box>

        <Divider />

        {/* Composer */}
        <Box
          sx={{
            p: 1.5,
            display: 'flex',
            gap: 1,
            alignItems: 'flex-end',
            bgcolor: 'background.paper',
          }}
        >
          <TextField
            inputRef={inputRef}
            fullWidth
            multiline
            maxRows={4}
            placeholder="Ask Blaack AI..."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
            size="small"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <IconButton
            color="primary"
            onClick={sendMessage}
            disabled={!prompt.trim() || loading}
            sx={{ mb: 0.25 }}
          >
            <SendIcon />
          </IconButton>
        </Box>
      </Drawer>
    </>
  );
};

export default AiCopilotDrawer;